import { useQuery } from '@tanstack/react-query';
import type { BooksHealthResponse } from '@accounting/shared';
import { api } from '../lib/api';
import { booksHealthKey } from './useBooksHealth';
import { useCompanies } from './useCompanies';
import { useBankAccountSummaries } from './useBank';
import { usePrintQueue } from './useCheckPrinting';
import { useActiveCompanyId } from '../stores/useCompanyStore';
import { todayIso } from '../lib/formatters';
import { useAssistantSummary } from './useAssistant';
import { useParticipationExceptions } from './useParticipation';

/**
 * Sidebar nudge counts — "how many things are waiting for me".
 *
 * Deliberately derived from the SAME books-health payload the Books Health page
 * renders, rather than from a second set of queries. A badge that disagreed
 * with the page it links to would be worse than no badge at all: you would
 * click "3" and land on a page showing 5.
 *
 * The page observer asks for fresh data on every visit (staleTime 0); this one
 * is content with a 5-minute-old number, so navigating around the app does not
 * re-run balance sheets and hash-chain verification for every company.
 */
const NUDGE_STALE_MS = 5 * 60 * 1000;

export interface Nudges {
  /** Everything Books Health found — the total across all checks. */
  booksHealth: number;
  /** The reconciliation slice: accounts never reconciled or overdue. */
  reconcile: number;
  /**
   * Bank-feed transactions awaiting a decision, for the ACTIVE company only —
   * because that is what the Bank Transactions page shows. A cross-company
   * count would send you to a page displaying a different number.
   */
  bankUnmatched: number;
  /**
   * Checks queued for the batch printer, across ALL companies — matching the
   * print queue page, which is cross-company precisely so one sheet can carry
   * several companies' checks.
   */
  checksToPrint: number;
  /** True when at least one finding is `fail` — provably wrong, not just drifted. */
  hasFailure: boolean;
  /**
   * Assistant accounts whose sign-in expired. Polled once a minute from a
   * lightweight summary endpoint, not derived from books health.
   */
  assistantReauth: number;
  /** Open attention-feed items (mail that needs the owner). */
  assistantAttention: number;
  /**
   * Participation sessions that could not be paired cleanly. Counted from the
   * same payload the Participation page lists under "Needs you".
   */
  participationExceptions: number;
}

export function useNudges(): Nudges {
  const { data: companies = [] } = useCompanies();
  const ids = companies.map((c) => c.id);
  const asOf = todayIso();
  const assistant = useAssistantSummary();
  const assistantReauth = assistant.data?.needsReauth ?? 0;
  const assistantAttention = assistant.data?.attentionOpen ?? 0;
  const { data: exceptions = [] } = useParticipationExceptions({ staleTime: NUDGE_STALE_MS, quiet: true });
  const participationExceptions = exceptions.length;

  const { data } = useQuery({
    queryKey: booksHealthKey(ids, asOf),
    enabled: ids.length > 0,
    staleTime: NUDGE_STALE_MS,
    refetchOnWindowFocus: false,
    // A failed health check must not surface as a red error in the chrome —
    // the page is where errors get explained.
    retry: false,
    queryFn: () =>
      api.get<BooksHealthResponse>('/books-health', {
        companyIds: ids.join(','),
        asOf,
      }),
  });

  // Company-scoped, unlike the health checks above: the page this badge links
  // to shows one company at a time.
  const activeCompanyId = useActiveCompanyId();
  const { data: summaries = [] } = useBankAccountSummaries(activeCompanyId);
  const bankUnmatched = summaries.reduce((n, s) => n + (s.unmatchedCount ?? 0), 0);

  const { data: printQueue } = usePrintQueue();
  const checksToPrint = printQueue?.totalCount ?? 0;

  if (!data) {
    return { booksHealth: 0, reconcile: 0, bankUnmatched, checksToPrint, hasFailure: false, assistantReauth, assistantAttention, participationExceptions };
  }

  const booksHealth = data.checks.reduce((n, c) => n + c.findings.length, 0);
  const reconcile =
    data.checks.find((c) => c.id === 'stale_reconciliations')?.findings.length ?? 0;
  const hasFailure = data.checks.some((c) => c.severity === 'fail' && c.findings.length > 0);

  return { booksHealth, reconcile, bankUnmatched, checksToPrint, hasFailure, assistantReauth, assistantAttention, participationExceptions };
}
